/**
 * Read a finished report without leaving the page (REVIEW-002 C3/C4).
 *
 * Until now the only thing you could do with a rendered report was download it, which
 * on a phone means handing the document to whatever PDF viewer the operating system
 * feels like -- and the review workflow of section 2.2 is exactly what a checker does
 * on a phone (14.3). So: fetch the bytes with the bearer token, hand the `blob:` URL
 * to an `<iframe>`, and keep Download beside it for the browsers that will not frame
 * a PDF at all.
 *
 * The approach is `components/support/AttachmentPreview` verbatim, for the same
 * reason it works there: the report endpoint sits behind the token (15.3), so an
 * `<iframe src>` pointing at the API would arrive with no credentials, and
 * `frame-src 'self' blob:` in deploy/nginx-app.conf is what allows the blob.
 */
import { Download, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useBlobUrl } from '@/components/support/useBlobUrl'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { downloadFile } from '@/lib/download'
import { formatBytes } from '@/lib/format'

export function ReportPreviewDialog({
  reportId,
  byteSize,
  pageCount,
  onClose,
}: {
  /** Null closes the dialog and releases the object URL. */
  reportId: number | null
  byteSize?: number
  pageCount?: number | null
  onClose: () => void
}) {
  const { t } = useTranslation(['common', 'report'])
  const path = reportId === null ? null : `/api/v1/reports/${reportId}/file`
  const blob = useBlobUrl(path)

  return (
    <Dialog open={reportId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[92dvh] w-[calc(100vw-1rem)] max-w-5xl flex-col gap-3 p-3 sm:p-4">
        <div className="flex min-w-0 items-center gap-2 pe-8">
          <DialogTitle className="min-w-0 flex-1 truncate text-sm font-medium">
            {t('report.preview_title')}
          </DialogTitle>
          <DialogDescription className="shrink-0 text-xs">
            {byteSize ? formatBytes(byteSize) : ''}
            {pageCount ? ` · ${t('report.pages', { count: pageCount })}` : ''}
          </DialogDescription>
        </div>

        <div className="flex min-h-[40dvh] flex-1 items-center justify-center overflow-auto rounded-md bg-surface-raised">
          {blob.status === 'loading' && (
            <span className="text-sm text-text-muted">{t('common.loading')}</span>
          )}
          {blob.status === 'error' && (
            <span className="p-4 text-center text-sm text-text-muted">
              {t('report.preview_failed')}
            </span>
          )}
          {blob.status === 'ready' && (
            <iframe
              src={blob.url}
              // An iframe's title is its accessible name, which jsx-a11y requires;
              // it is not the hover tooltip the project rule guards against.
              // eslint-disable-next-line no-restricted-syntax
              title={t('report.preview_title')}
              className="h-[70dvh] w-full rounded-md border-0 bg-surface"
            />
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          {blob.status === 'ready' && (
            <Button asChild variant="secondary" size="sm">
              <a href={blob.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} aria-hidden />
                {t('support.attachments.open_tab')}
              </a>
            </Button>
          )}
          {path && (
            <Button variant="primary" size="sm" onClick={() => void downloadFile(path)}>
              <Download size={14} aria-hidden />
              {t('action.download')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
